/**
 * routeBuilder.js — Genere les routes react-router a partir de ROUTE_CONFIG
 * (source de verite unique).
 *
 * - RouteGuard : protege une route selon ses roles (l'auth globale est deja
 *   assuree en amont par ProtectedRoute dans le Root du router).
 * - buildRouteChildren : produit le tableau de routes enfants du Root.
 * - buildPublicRoutes  : produit les routes publiques hors Root.
 */

import React, { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { ROUTE_CONFIG } from 'config/routes.config';
import { ROLES, hasAnyRole } from 'config/roles';
import { useUser } from 'services/context/UserContext';

export const useUserRoles = () => {
  const { user } = useUser();
  return user?.roles || [];
};

/** Garde d'acces par roles. Redirige vers /access-denied si non autorise. */
export function RouteGuard({ roles, children }) {
  const userRoles = useUserRoles();
  if (!hasAnyRole(userRoles, roles)) {
    return <Navigate to="/access-denied" replace />;
  }
  return children;
}

/** Construit l'element protege d'une route. */
const guardedElement = (roles, Component) => (
  <RouteGuard roles={roles}>
    <Component />
  </RouteGuard>
);

/** Developpe une entree de config (et ses children) en routes soeurs. */
const expand = (route) => {
  if (route.redirect !== undefined) {
    return [{ path: route.path, element: <Navigate to={`/${route.redirect}`} replace /> }];
  }
  const roles = route.roles || ROLES.ADMIN.id; // par defaut, les routes sont admin-only (sauf si roles: [] explicite)
  const routes = [{ path: route.path, element: guardedElement(roles, route.component) }];
  (route.children || []).forEach((child) => {
    const Component = child.component || route.component;
    const childRoles = child.roles || route.roles;
    routes.push({
      path: `${route.path}/${child.path}`,
      element: guardedElement(childRoles, Component),
    });
  });
  return routes;
};

/** Routes enfants du Root, generees depuis ROUTE_CONFIG (hors public). */
export const buildRouteChildren = () =>
  ROUTE_CONFIG.filter((r) => !r.public).flatMap(expand);

/** Routes publiques hors Root (pas de ProtectedRoute), generees depuis ROUTE_CONFIG. */
export const buildPublicRoutes = () =>
  ROUTE_CONFIG.filter((r) => r.public).map((r) => {
    const C = r.component;
    return {
      path: r.path,
      element: (
        <Suspense fallback={null}>
          <C />
        </Suspense>
      ),
    };
  });
